import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import inventoryService from '../../services/InventoryService.js';
import { Package, Plus, Filter } from "lucide-react";

const InventoryList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const perPage = 10;

  const categories = inventoryService.getCategories();
  const statuses = inventoryService.getStatuses();

  // Dynamic filter effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchItems();
    }, 300);

    return () => clearTimeout(timer);
  }, [search, categoryFilter, statusFilter, warehouseFilter]);

  // Fetch items when page changes
  useEffect(() => {
    if (currentPage !== 1) {
      fetchItems();
    }
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (categoryFilter) filters.category = categoryFilter;
      if (statusFilter) filters.status = statusFilter;
      if (warehouseFilter) filters.warehouse = warehouseFilter;

      const response = await inventoryService.getAll(currentPage, perPage, filters);
      setItems(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load inventory items');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (inventoryId, itemName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      'Delete Inventory Item'
    );
    
    if (confirmed) {
      try {
        await inventoryService.delete(inventoryId);
        Alert.success('Inventory item deleted successfully!');
        
        if (items.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchItems();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete inventory item');
      }
    }
  };

  const toggleDropdown = (inventoryId) => {
    setActiveDropdown(activeDropdown === inventoryId ? null : inventoryId);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'In Stock': 'bg-green-100 text-green-700',
      'Low Stock': 'bg-yellow-100 text-yellow-700',
      'Out of Stock': 'bg-red-100 text-red-700',
      'On Order': 'bg-blue-100 text-brand',
      'Reserved': 'bg-purple-100 text-purple-700',
      'Damaged': 'bg-blue-100 text-blue-700',
      'Expired': 'bg-gray-100 text-gray-700',
      'Discontinued': 'bg-gray-100 text-gray-700',
      'Under Maintenance': 'bg-indigo-100 text-indigo-700'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-700';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryFilter('');
    setStatusFilter('');
    setWarehouseFilter('');
    setCurrentPage(1);
  };

  const hasActiveFilters = search || categoryFilter || statusFilter || warehouseFilter;

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex h-screen bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Package className="w-5 h-5 text-buttonBg" />
                      Inventory
                    </h1>
                  </div>
                  <button
                    onClick={() => navigate('/inventory/add')}
                    className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonTxt bg-buttonBg hover:text-buttonBg hover:bg-buttonTxt border 
border-current rounded-sm  transition-all duration-300 font-medium whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4" /> Add Item
                  </button>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-0 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-tertiary font-bold"/>
                      <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                        className="sm:hidden text-blue-500 font-medium text-sm"
                      >
                        {showMobileFilters ? 'Hide' : 'Show'} Filters
                      </button>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
                          title="Clear all filters"
                        >
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            className="h-5 w-5" 
                            viewBox="0 0 20 20" 
                            fill="currentColor"
                          >
                            <path 
                              fillRule="evenodd" 
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                              clipRule="evenodd" 
                            />
                          </svg>
                          <span className="hidden sm:inline text-sm font-medium">Clear</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={`${showMobileFilters ? 'block' : 'hidden'} sm:block`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Search</label>
                        <input
                          type="text"
                          placeholder="Item name..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Category</label>
                        <select
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-sm font-medium text-tertiary"
                        >
                          <option value="">All Categories</option>
                          {categories.map((cat) => (
                            <option key={cat.value} value={cat.value}>
                              {cat.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Status</label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-sm font-medium text-tertiary"
                        >
                          <option value="">All Status</option>
                          {statuses.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-tertiary mb-1 sm:mb-2">Warehouse</label>
                        <input
                          type="text"
                          placeholder="Warehouse location..."
                          value={warehouseFilter}
                          onChange={(e) => setWarehouseFilter(e.target.value)}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table - Desktop View */}
                <div className="bg-white rounded-sm shadow-sm">
                  {loading ? (
                    <div className="p-8 text-center text-gray-600">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      Loading inventory items...
                    </div>
                  ) : items.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">No inventory items found</div>
                  ) : (
                    <>
                      <div className="hidden md:block w-full">
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">IMAGE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">ITEM ID</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">ITEM NAME</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">CATEGORY</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">QUANTITY</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">UNIT PRICE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">TOTAL VALUE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">STATUS</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">WAREHOUSE</th>
                              <th className="px-4 py-3 text-center text-xs lg:text-sm font-semibold whitespace-nowrap">ACTIONS</th>
                            </tr>
                          </thead>

                          <tbody className="divide-y divide-gray-200">
                            {items.map((item) => (
                              <tr key={item.inventory_id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-4 py-3 text-center">
                                  <div 
                                    className="w-10 h-10 rounded-md border-2 border-gray-200 flex items-center justify-center bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/inventory/view/${item.inventory_id}`);
                                    }}
                                  >
                                    {item.item_image_base64 ? (
                                      <img
                                        src={`data:image/jpeg;base64,${item.item_image_base64}`}
                                        alt={item.item_name}
                                        className="w-full h-full rounded-md object-cover"
                                      />
                                    ) : (
                                      <span className="text-2xl">📦</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center font-semibold text-secondary">
                                  <button
                                    onClick={() => navigate(`/inventory/view/${item.inventory_id}`)}
                                    className="text-sm text-secondary hover:underline font-semibold"
                                  >
                                    {item.inventory_id}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-center text-secondary font-semibold">
                                  <button
                                    onClick={() => navigate(`/inventory/view/${item.inventory_id}`)}
                                    className="font-semibold text-secondary hover:underline truncate max-w-[200px] block"
                                  >
                                    {item.item_name}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-center text-sm text-secondary font-semibold">{item.category}</td>
                                <td className="px-4 py-3 text-center text-sm text-secondary font-semibold">{item.quantity} {item.unit}</td>
                                <td className="px-4 py-3 text-center text-sm text-secondary font-semibold">{formatCurrency(item.unit_price)}</td>
                                <td className="px-4 py-3 text-center text-sm font-semibold text-secondary">{formatCurrency(item.total_value)}</td>
                                <td className="px-4 py-3 text-center">
                                  <span className={`px-3 py-1 text-secondary text-sm font-semibold`}>
                                    {item.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center text-sm text-gray-700 truncate max-w-[150px]">{item.warehouse_location}</td>
                                <td className="px-4 py-3 text-center relative">
                                  <button
                                    onClick={() => toggleDropdown(item.inventory_id)}
                                    className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                                    title="Actions"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="h-5 w-5 text-gray-600"
                                      fill="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle cx="12" cy="5" r="2"/>
                                      <circle cx="12" cy="12" r="2"/>
                                      <circle cx="12" cy="19" r="2"/>
                                    </svg>
                                  </button>

                                  {activeDropdown === item.inventory_id && (
                                    <div
                                      ref={dropdownRef}
                                      className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10"
                                    >
                                      <button
                                        onClick={() => {
                                          navigate(`/inventory/view/${item.inventory_id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-center text-sm hover:bg-gray-50 flex gap-2"
                                      >
                                        👁️ View Details
                                      </button>
                                      <button
                                        onClick={() => {
                                          navigate(`/inventory/edit/${item.inventory_id}`);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-center text-sm hover:bg-gray-50 flex gap-2"
                                      >
                                        ✏️ Edit
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleDelete(item.inventory_id, item.item_name);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full px-4 py-2 text-center text-sm text-red-600 hover:bg-red-50 flex gap-2"
                                      >
                                        🗑️ Delete
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination - Desktop */}
                      <div className="hidden md:flex bg-gray-50 px-6 py-4 justify-between items-center border-t">
                        <div className="text-sm text-gray-700">
                          Showing {items.length} item{items.length !== 1 ? 's' : ''}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            ← Previous
                          </button>
                          <span className="px-4 py-2 text-sm text-gray-700">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Card View - Mobile/Tablet */}
                <div className="md:hidden space-y-3 sm:space-y-4">
                  {loading ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      Loading inventory items...
                    </div>
                  ) : items.length === 0 ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No inventory items found</div>
                  ) : (
                    <>
                      {items.map((item) => (
                        <div key={item.inventory_id} className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
                          <div className="flex items-start gap-3 sm:gap-4 mb-3">
                            <div 
                              className="w-14 h-14 sm:w-16 sm:h-16 rounded-md border-2 border-gray-200 flex items-center justify-center bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/inventory/view/${item.inventory_id}`);
                              }}
                            >
                              {item.item_image_base64 ? (
                                <img
                                  src={`data:image/jpeg;base64,${item.item_image_base64}`}
                                  alt={item.item_name}
                                  className="w-full h-full rounded-md object-cover"
                                />
                              ) : (
                                <span className="text-4xl">📦</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate">{item.item_name}</h3>
                              <p className="text-xs sm:text-sm text-gray-600">{item.inventory_id}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(item.status)}`}>
                                  {item.status}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                            <div>
                              <span className="text-gray-500">Category:</span>
                              <p className="text-gray-900 font-medium">{item.category}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Quantity:</span>
                              <p className="text-gray-900 font-medium">{item.quantity} {item.unit}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Unit Price:</span>
                              <p className="text-gray-900 font-medium">{formatCurrency(item.unit_price)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Total Value:</span>
                              <p className="text-gray-900 font-semibold">{formatCurrency(item.total_value)}</p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/inventory/view/${item.inventory_id}`)}
                              className="flex-1 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs sm:text-sm font-medium"
                            >
                              👁️ View
                            </button>
                            <button
                              onClick={() => navigate(`/inventory/edit/${item.inventory_id}`)}
                              className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDelete(item.inventory_id, item.item_name)}
                              className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Mobile Pagination */}
                      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                        <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                          Showing {items.length} item{items.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                          >
                            ← Previous
                          </button>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryList;